import { SxProp } from 'theme-ui';
import { IInpostWidget } from '@features/checkout/typescript';
import { TDicountType } from '@features/userPanel/typescript';
import { CartProductPricesType } from '@features/cart/typescript';

interface IPhotoSlurpWidgetSettings {
  [key: string]: {
    addPhotosImg: string;
    albumId: number;
    emptyText?: string;
    emptyThreshold?: number;
    lang?: string;
    pageLimit?: number;
    productId: number;
    randomOrder?: boolean;
    shopThisLookText?: string;
    theme: string;
    widgetId: string;
    widgetType: string;
  };
}

interface IFlowboxSettings {
  container: string;
  key: string;
  locale: string;
  productId: number;
}

export type TGAEvent = 'ga4Event';

export type TEvent =
  | 'view_cart'
  | 'purchase'
  | 'add_to_cart'
  | 'remove_from_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'view_item_list'
  | 'view_item'
  | 'select_item'
  | 'add_payment_info';

export type TEventAction =
  | 'begin_login'
  | 'show_mini_cart'
  | 'payment_screen'
  | 'enter_postal_code'
  | 'Logowanie'
  | 'begin_order_summary';

export type TBaseDataLayer =
  | {
      event?: TGAEvent;
      eventAction?: TEventAction;
    }
  | {
      event?: TEvent;
      eventAction?: never;
    };

export type TItemsDataLayer = {
  value: number;
  currency: string;
  items: Array<unknown>;
};

export type TViewItemsDataLayer = {
  item_list_id: string;
  item_list_name: string;
  items: Array<unknown>;
};

export interface ICategoriesItems {
  [key: string]: string;
}

export type ICustomDataLayer<T, K extends string[]> = T & {
  [KKey in K[number]]: string;
};

export type TTransactionDataLayer = TItemsDataLayer & {
  transaction_id: string;
  shipping: number;
  isCustNew: boolean;
  payMethod?: string;
  coupon: TDicountType | string | null;
  email?: string | null;
  phone?: string | null;
};

export type TDataLayer = TBaseDataLayer & {
  ecommerce?: TItemsDataLayer | TTransactionDataLayer | TViewItemsDataLayer | null;
};

interface LiveChatWidget {
  call(method: string, options?: Record<string, string | number>): void;
}

interface ICartStoreProduct {
  productId: string;
  prices: CartProductPricesType;
}

interface ICartStore {
  products: Map<string, ICartStoreProduct>;
}

declare global {
  interface Window {
    photoSlurpWidgetSettings: IPhotoSlurpWidgetSettings;
    flowbox: (type: string, settings: IFlowboxSettings) => void;
    Pswidget: unknown;
    dataLayer: Array<TDataLayer>;
    onPoint: (onPoint: IInpostWidget) => void;
    getPublicEnv: (key: string) => string;
    properties: unknown;
    ga?: unknown;
    gtag?: unknown;
    fbq?: unknown;
    _gdeaq?: unknown;
    pintrk?: unknown;
    rtbhEvents?: unknown;
    ttq?: {
      track: unknown;
    };
    wph?: unknown;
    LiveChatWidget?: LiveChatWidget;
    __CART_STORE__?: ICartStore;
  }
}

export interface IPerformanceEntry extends PerformanceEntry {
  type: string;
}

export type TPublication = 'prod' | 'test';

export type TLocale = 'pl' | 'en' | 'ro' | 'bg' | 'fr' | 'de';

export type TLocaleISO = 'pl_PL' | 'en_EN' | 'ro_RO' | 'bg_BG' | 'fr_FR' | 'de_DE';

export type TRegion = 'MX' | 'PL' | 'EN' | 'EUR' | 'RO' | 'BG';

export type TLogger = 'info' | 'warn' | 'error' | 'trace' | 'debug';

export type TRouterQuery = string | string[] | undefined;

export type TBreadcrumbHref =
  | string
  | {
      query: { slug?: TRouterQuery; mainCategory?: TRouterQuery };
      pathname: string;
    };

export interface IBreadcrumbStep {
  name: string;
  href?: TBreadcrumbHref;
  id: number;
  as?: string;
  siblings?: IBreadcrumbStep[];
}

export interface IStaticCategoryPath {
  params: { slug: string | string[]; mainCategory: string };
  locale: string;
}

export interface IStaticMainPath {
  params: { mainCategory: string };
  locale: string;
}

export interface IStore {
  name: string;
  subName: string;
  street: string;
  postalCode: string;
  city: string;
  email: string;
  telephone: string;
  workingHours: {
    type: string;
    hours: string;
  }[];
  storeLink: string;
  directionsLink: string;
}

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T>, SxProp {
    publication?: string;
    offer?: string;
    pricelists?: string;
    storages?: string;
    layoutBarsHeight?: number;
  }
}

declare global {
  namespace JSX {
    interface IntrinsicAttributes extends SxProp {}
  }
}

export type ICachePathMap = {
  [key in TLocale]: string;
};

export interface IPlatformApiError {
  exception: string;
  type: string;
  codename: string;
  code: number;
  message: string;
  data: Record<symbol, unknown>;
}

export interface IPlatformApiErrorObject {
  message: string;
  extensions: unknown;
}

export interface IPlatformApiErrorV2 {
  errors: Array<IPlatformApiErrorObject>;
}

export interface IApiError {
  error: boolean;
  status: number;
  success?: boolean;
  message?: string;
  json?: IPlatformApiError | IPlatformApiErrorV2;
}

export interface ISeo {
  title: string;
  description: string;
  'og:type': string;
  'og:title': string;
  'og:description': string;
  'og:image': string;
  'og:image.alt': string;
  imagePaths?: string[];
}

export interface ISeoImage {
  url: string;
  alt: string;
  type: string;
}

interface ISwaggerInfo {
  title: string;
  version: string;
}

interface ISwaggerSchemaRecord {
  properties: Record<string, Record<string, string>>;
  required: string[];
  additionalProperties: boolean;
  title: string;
  type: string;
}

interface ISwaggerComponents {
  schemas: Record<string, ISwaggerSchemaRecord>;
}

export interface ISwaggerSpec {
  openapi: string;
  info: ISwaggerInfo;
  paths: Record<string, string>;
  components: ISwaggerComponents;
  tags: unknown[];
}

export interface ISitemapProduct {
  id: number;
  name: string;
  flags: Record<string, string | null>[];
}

export interface IPagination {
  count: number;
  offset: number;
  limit: number;
}

interface IMarketingColor {
  hex: string;
  file: string;
  name: string;
}

interface IProductVariant {
  productCartId: number;
  productCartFile: string;
  productCartName: string;
}

export interface IProductSwatch {
  products: IProductVariant[];
  marketingColors: IMarketingColor[];
  rawFeatureValueCode: string;
  rawFeatureValueFile: string;
  rawFeatureValueName: string;
}

export type TSwatchShape = 'circle' | 'square';

export type TSwatchSize = 'normal' | 'big' | 'small';

export interface ISwatch {
  color?: string;
  shape?: TSwatchShape;
  image?: string;
  clickable?: boolean;
  active: boolean;
  size: TSwatchSize;
  hoverable?: boolean;
}

export enum WindowType {
  fullhd = 'fullhd',
  widescreen = 'widescreen',
  desktop = 'desktop',
  tablet = 'tablet',
  mobile = 'mobile',
}

export interface IGeoData {
  country?: string;
  region?: string;
  city?: string;
  latitude?: string;
  longitude?: string;
}

export type TRegionsLocale =
  | 'pl_PL'
  | 'en_EN'
  | 'fr_FR'
  | 'et_ET'
  | 'lt_LT'
  | 'cs_CS'
  | 'ro_RO'
  | 'lv_LV'
  | 'de_DE'
  | 'nl_NL'
  | 'ru_RU'
  | 'it_IT'
  | 'es_ES'
  | 'bg_BG'
  | 'uk_UK';

export type TRegionsEnviroments = 'prod' | 'test' | 'staging';

export type TRegionServicesKey = 'ECOMMERCE' | 'VOXBOX';

export interface IRegionService {
  key: string;
  urls: Record<TRegionsEnviroments, string>;
}
export interface IDefaultLanguage {
  code: TRegionsLocale;
  names: Record<TRegionsLocale, string>;
  urls: Record<TRegionsEnviroments, string>;
  sale: boolean | null;
}

export type TRegionServices = Array<IRegionService>;
export interface IOffer {
  key: string;
  name: string;
  defaultStorage: Record<'key', string>;
  defaultPricelist: Record<'key', string>;
  code: string;
  ecommerce: boolean;
  sale: boolean;
  fake: boolean;
  defaultLanguage: IDefaultLanguage;
  availableLanguages: Record<TRegionsLocale, IDefaultLanguage>;
  services: Array<IRegionService>;
}

export interface IRegion {
  key: string;
  code: string;
  names: Record<TRegionsLocale, string>;
  offers: IOffer[];
  services: Array<IRegionService>;
  position: number;
}

export interface IRegionDataLanguage {
  name: string;
  code: string;
  url: string;
  locale: string;
  sale: boolean | null;
}
export interface IRegionData {
  name: string;
  key: string;
  sale: boolean;
  offer: string;
  languages: Array<IRegionDataLanguage>;
  position: number;
}

export interface ISiteTypesDataLanguage {
  name: string;
  code: string;
  urls: Record<TRegionsEnviroments, string>;
}

export interface ISiteTypesData {
  code: string;
  key: string;
  name: string;
  subname: string;
  siteType: string;
  isShop: boolean;
  urls: Record<TRegionsEnviroments, string>;
  languages: Array<ISiteTypesDataLanguage>;
}

export enum ContactExtEventType {
  Purchase = 'PURCHASE',
  Cart = 'CART',
  Transaction = 'TRANSACTION',
  Cancellation = 'CANCELLATION',
  Return = 'RETURN',
  Visit = 'VISIT',
  Other = 'OTHER',
  PhoneCall = 'PHONE_CALL,',
  Reservation = 'RESERVATION',
  Canceled = 'CANCELLED',
  Activation = 'ACTIVATION',
  Meeting = 'MEETING',
  Offer = 'OFFER',
  Download = 'DOWNLOAD',
  Login = 'LOGIN',
  Survey = 'SURVEY',
}
export interface IContactEvent {
  date: string;
  contactExtEventType: ContactExtEventType;
  products: string;
  value: string;
  location: string;
  externalId: string;
  description: string;
  [key: string]: string;
}

export interface ISurveyBody {
  referenceId: string;
  type: string;
  answers: {
    didAdvisorHelpsYouBeforeBuyingOnline: boolean;
    helpingPointOfSaleId: string;
  };
}

export interface IFile {
  category: string;
  category_id: number;
  file: string;
  file_type: string;
  name: string;
}
